import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { wabaApi } from "../../lib/wabaApi";
import { DialogPanel } from "../shared/DialogPanel";
import { CartIcon, InstagramIcon, MessageIcon, MessengerIcon, TagIcon, WhatsAppIcon, XIcon } from "../shared/Icons";
import { backdropClose } from "../shared/backdrop";

interface LogEntry {
  id: string;
  direction: string;
  event_type: string;
  wamid: string | null;
  from_number: string | null;
  to_number: string | null;
  contact_name: string | null;
  payload: string;
  send_status: string | null;
  send_error: string | null;
  views: string | null; // JSON {request?, wa?, v3?} — 45 dias
  channel: string | null; // whatsapp | instagram | messenger | facebook | mercadolivre | olx | webmotors | email
  created_at: string;
}

// Canal de cada linha (log antigo: o back deduz pelo id da mensagem)
const CHANNEL: Record<string, { label: string; Icon: typeof WhatsAppIcon }> = {
  whatsapp: { label: "WhatsApp", Icon: WhatsAppIcon },
  instagram: { label: "Instagram", Icon: InstagramIcon },
  messenger: { label: "Messenger", Icon: MessengerIcon },
  facebook: { label: "Facebook (comentários)", Icon: MessageIcon },
  mercadolivre: { label: "Mercado Livre", Icon: CartIcon },
  olx: { label: "OLX", Icon: TagIcon },
  webmotors: { label: "Webmotors", Icon: TagIcon },
  email: { label: "E-mail", Icon: MessageIcon },
  portais: { label: "Portais (lead)", Icon: TagIcon },
};

// Canais fora da Meta: não vão ao agente; a carga original é a da plataforma
const FORA: Record<string, string> = {
  mercadolivre: "Mercado Livre", olx: "OLX", webmotors: "Webmotors", email: "E-mail", portais: "Portais",
};

type View = "v3" | "wa" | "request";
const VIEWS: View[] = ["v3", "wa", "request"];
// "wa" = o que vai ao agente, rotulado pelo canal da linha (Formato Instagram, Formato OLX…)
const viewLabel = (k: View, channel: string | null) =>
  k === "v3" ? "Webhook v3" : k === "request" ? "Requisição completa"
  : `Formato ${CHANNEL[channel ?? "whatsapp"]?.label ?? channel ?? "WhatsApp"}`;

function parse(raw: string | null): any {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return raw; }
}

/** O que mostrar em cada visão: o JSON, ou o motivo de não ter. */
function viewOf(log: LogEntry, view: View): { json?: unknown; caption: string } {
  const views = parse(log.views) ?? {};
  const payload = parse(log.payload);
  const out = log.direction === "outbound";
  const metaDm = !out && payload && typeof payload === "object" && "sender" in payload; // item cru do IG/Messenger
  if (view === "v3") {
    return views.v3
      ? { json: views.v3, caption: "Envelope que saiu no webhook v3." }
      : { caption: "Sem envelope v3: o worker ainda não processou este evento, ou log anterior a 05/10." };
  }
  if (view === "wa") {
    if (FORA[log.channel ?? ""])
      return { caption: "Canal fora da Meta: não vai ao agente (veja a carga original em Requisição completa)." };
    if (views.wa) return { json: views.wa, caption: "O que vai ao agente." };
    // inbound fora do WhatsApp sem conversão gravada (comentário, por ex.) não vai ao agente: é a carga crua
    if (out || (!metaDm && (log.channel ?? "whatsapp") === "whatsapp")) return { json: payload, caption: "O que vai ao agente." };
    return { caption: "Este tipo não vai ao agente (ou log anterior a 05/10)." };
  }
  const origem = FORA[log.channel ?? ""] ?? "Meta";
  if (out) return { json: views.request ?? payload, caption: `Corpo enviado à ${origem}.` };
  return { json: payload, caption: `Carga recebida ${origem === "Mercado Livre" ? "do" : "da"} ${origem}.` };
}

/** send_error pode ser JSON estruturado da Meta (as_detail) ou string crua. */
function formatSendError(raw: string): string {
  try {
    const e = JSON.parse(raw);
    const parts = [
      e.error,
      e.meta_code != null ? `código ${e.meta_code}${e.meta_subcode != null ? `.${e.meta_subcode}` : ""}` : null,
      e.meta_message && e.meta_message !== e.error ? e.meta_message : null,
      e.meta_details,
    ].filter(Boolean);
    return parts.length ? parts.join(" — ") : raw;
  } catch {
    return raw;
  }
}

interface Props { connectionId: string; onClose: () => void; initialChannel?: string; }

const DIR_COLOR: Record<string, string> = { inbound: "var(--info)", outbound: "var(--violet)", group: "var(--ok)" };
const STATUS_COLOR: Record<string, string> = { sent: "var(--ok)", failed: "var(--danger)" };

export function MessageLog({ connectionId, onClose, initialChannel }: Props) {
  const [channel, setChannel] = useState(initialChannel && CHANNEL[initialChannel] ? initialChannel : "");
  const [direction, setDirection] = useState("");
  const [eventType, setEventType] = useState("");
  const [fromNumber, setFromNumber] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [view, setView] = useState<View>("wa");
  const [page, setPage] = useState(0);
  const LIMIT = 30;

  const params = new URLSearchParams({ limit: String(LIMIT), offset: String(page * LIMIT) });
  if (channel) params.set("channel", channel);
  if (direction) params.set("direction", direction);
  if (eventType) params.set("event_type", eventType);
  if (fromNumber) params.set("from_number", fromNumber);
  // dia em horário de Brasília → limites em UTC (created_at é UTC naive)
  const toUtc = (d: string, t: string) => new Date(`${d}T${t}-03:00`).toISOString().slice(0, 19).replace("T", " ");
  if (dateFrom) params.set("date_from", toUtc(dateFrom, "00:00:00"));
  if (dateTo) params.set("date_to", toUtc(dateTo, "23:59:59"));

  const { data, isLoading } = useQuery({
    queryKey: ["waba-logs", connectionId, channel, direction, eventType, fromNumber, dateFrom, dateTo, page],
    queryFn: () => wabaApi.get<{ total: number; logs: LogEntry[] }>(`/connections/${connectionId}/logs?${params}`),
    refetchInterval: 5000,
  });

  const logs = data?.logs ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  const inputStyle: React.CSSProperties = { padding: "5px 9px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 5, color: "var(--ink)", fontSize: 12, outline: "none" };
  const selectStyle: React.CSSProperties = { ...inputStyle, cursor: "pointer" };

  function formatTime(iso: string) {
    const utc = iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z";
    return new Date(utc).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "America/Sao_Paulo" });
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}
      {...backdropClose(() => { onClose(); })}>
      <DialogPanel onClose={() => onClose()} label="Log de Mensagens" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 700, maxHeight: "85vh", display: "flex", flexDirection: "column", gap: 14 }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Log de Mensagens</span>
          <button aria-label="Fechar" onClick={onClose} style={{ background: "transparent", border: "none", color: "var(--ink-muted)", fontSize: 18, cursor: "pointer" }}><XIcon size={16} /></button>
        </div>

        {/* Filtros */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select style={selectStyle} value={channel} aria-label="Canal" onChange={e => { setChannel(e.target.value); setPage(0); }}>
            <option value="">Todos os canais</option>
            {Object.entries(CHANNEL).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
          </select>
          <select style={selectStyle} value={direction} onChange={e => { setDirection(e.target.value); setPage(0); }}>
            <option value="">Todos</option>
            <option value="inbound">Entrada</option>
            <option value="outbound">Saída</option>
            <option value="group">Grupos</option>
          </select>
          <select style={selectStyle} value={eventType} onChange={e => { setEventType(e.target.value); setPage(0); }}>
            <option value="">Todos os tipos</option>
            {["text","listing","template","carousel","flow","interactive","image","audio","video","document","sticker","location","contacts","unsupported","call_permission_reply","status_sent","status_delivered","status_read","status_failed"].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <input style={{ ...inputStyle, width: 140 }} placeholder="Número..." value={fromNumber} onChange={e => { setFromNumber(e.target.value); setPage(0); }} />
          <input type="date" style={inputStyle} value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }} title="De" />
          <input type="date" style={inputStyle} value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }} title="Até" />
          <span style={{ fontSize: 12, color: "var(--ink-muted)", alignSelf: "center", marginLeft: "auto" }}>
            {total} registro{total !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Lista */}
        <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
          {isLoading && <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Carregando...</span>}
          {logs.map(log => (
            <div key={log.id} style={{ background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6 }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", cursor: "pointer" }}
                onClick={() => setExpanded(expanded === log.id ? null : log.id)}
              >
                {(() => {
                  const c = CHANNEL[log.channel ?? ""];
                  return c ? (
                    <span title={c.label} aria-label={c.label} style={{ display: "inline-flex", color: "var(--ink-secondary)" }}>
                      <c.Icon size={14} />
                    </span>
                  ) : <span style={{ width: 14 }} />;
                })()}
                <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 4, background: "var(--surface-raised)", color: DIR_COLOR[log.direction] ?? "var(--ink-soft)" }}>
                  {log.direction === "inbound" ? "↓ IN" : log.direction === "group" ? "◎ GRUPO" : "↑ OUT"}
                </span>
                <span style={{ fontSize: 12, color: "var(--ink-secondary)", minWidth: 100 }}>{log.event_type}</span>
                <span style={{ fontSize: 12, color: "var(--ink-muted)", flex: 1 }}>
                  {log.direction === "inbound" || (log.direction === "group" && log.from_number)
                    ? (log.contact_name ? `${log.contact_name} (${log.from_number})` : log.from_number)
                    : log.direction === "group" ? "grupo" : `→ ${log.to_number}`}
                </span>
                {log.send_status && (
                  <span style={{ fontSize: 10, color: STATUS_COLOR[log.send_status] ?? "var(--ink-soft)" }}>{log.send_status}</span>
                )}
                <span style={{ fontSize: 10, color: "var(--line-max)" }}>{formatTime(log.created_at)}</span>
              </div>
              {expanded === log.id && (
                <>
                  {log.send_error && (
                    <div style={{ margin: "0 12px 8px", padding: "8px 10px", fontSize: 11, color: "var(--danger)", background: "var(--danger-soft)", border: "1px solid var(--danger)", borderRadius: 6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {formatSendError(log.send_error)}
                    </div>
                  )}
                  {(() => {
                    const v = viewOf(log, view);
                    return (
                      <div style={{ borderTop: "1px solid var(--line)" }}>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", padding: "8px 12px 6px", flexWrap: "wrap" }}>
                          {VIEWS.map(k => (
                            <button key={k} onClick={() => setView(k)}
                              style={{ fontSize: 11, padding: "3px 9px", borderRadius: 5, cursor: "pointer",
                                border: `1px solid ${view === k ? "var(--violet)" : "var(--line-bold)"}`,
                                background: view === k ? "var(--surface-raised)" : "transparent",
                                color: view === k ? "var(--ink)" : "var(--ink-muted)" }}>
                              {viewLabel(k, log.channel)}
                            </button>
                          ))}
                          <span style={{ fontSize: 10, color: "var(--ink-muted)", marginLeft: 4 }}>{v.caption}</span>
                        </div>
                        {v.json !== undefined && (
                          <pre style={{ margin: 0, padding: "0 12px 10px", fontSize: 10, color: "var(--ink-soft)", whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 260, overflow: "auto" }}>
                            {typeof v.json === "string" ? v.json : JSON.stringify(v.json, null, 2)}
                          </pre>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          ))}
          {!isLoading && logs.length === 0 && (
            <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Nenhum registro encontrado com os filtros aplicados.</span>
          )}
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ background: "transparent", border: "1px solid var(--line-bold)", borderRadius: 6, color: page === 0 ? "var(--line-bold)" : "var(--ink-secondary)", fontSize: 12, padding: "5px 12px", cursor: page === 0 ? "default" : "pointer" }}>
              ← Anterior
            </button>
            <span style={{ fontSize: 12, color: "var(--ink-muted)", alignSelf: "center" }}>{page + 1}/{totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ background: "transparent", border: "1px solid var(--line-bold)", borderRadius: 6, color: page >= totalPages - 1 ? "var(--line-bold)" : "var(--ink-secondary)", fontSize: 12, padding: "5px 12px", cursor: page >= totalPages - 1 ? "default" : "pointer" }}>
              Próximo →
            </button>
          </div>
        )}
      </DialogPanel>
    </div>
  );
}
